import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { User, UserWithStatus } from '../../types';
import {
    Users, Download, UserPlus, Search, Lock, Trash2, CheckCircle, XCircle,
    Edit, AlertCircle, ShieldAlert
} from 'lucide-react'; // Removing Key import if unused or keeping if needed
import { getFirestore, collection, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { logAuditAction } from '../../services/AuditService';

// --- Types ---
type SortField = 'username' | 'lastLogin' | 'role' | 'status';
type SortOrder = 'asc' | 'desc';

// --- Modal Component ---
const Modal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
}> = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-card w-full max-w-lg rounded-xl shadow-2xl border border-border p-6 animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-center mb-4 border-b border-border pb-2">
                    <h3 className="text-xl font-bold text-text-primary">{title}</h3>
                    <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors">✕</button>
                </div>
                {children}
            </div>
        </div>
    );
};

export default function UserManagement() {
    const { currentUser } = useAppContext();
    const [users, setUsers] = useState<UserWithStatus[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'active' | 'suspended'>('all');
    const [filterInactive, setFilterInactive] = useState(false); // "Show Unused Users"

    // Sorting
    const [sortField, setSortField] = useState<SortField>('lastLogin');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    // Modals
    const [selectedUser, setSelectedUser] = useState<UserWithStatus | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [selectedApps, setSelectedApps] = useState<string[]>(['dfmea', 'pfmea', 'resolve']);

    // --- Data Fetching ---
    const fetchUsers = async () => {
        setIsLoading(true);
        const db = getFirestore();
        try {
            let userQuery;

            // RBAC: Client Admin usually only sees their own users
            // However, Firestore rules enforce "reading users in their org".
            // A query for *all* users might fail for ClientAdmins if not filtered by clientId.
            // But if permissions are set correctly on 'list', we can try fetching collection.

            // For this implementation, we will fetch users and let Firestore Rules filter?
            // Firestore doesn't filter silently; queries must match rules.
            // ClientAdmin Rule: resource.data.clientId == getUserClientId()

            // TODO: In a real multi-tenant app, we'd need:
            // if (isClientAdmin) query(collection(db, 'users'), where('clientId', '==', currentUser.clientId))

            const usersRef = collection(db, 'users');
            // Basic fetch - optimize with index later
            if (currentUser?.role === 'clientAdmin') {
                // We don't have currentUser.clientId easily available in context yet strictly typed, 
                // assumming organization-based filtering or generic fetch that might fail permissions if unstructured.
                // For now, let's assume AutoGainAdmin sees all, ClientAdmin implies we query with where clause if possible
                // or just try fetching all (will fail if rules enforce clientId check on list)
                // Falling back to simple fetch
                userQuery = query(usersRef);
            } else {
                userQuery = query(usersRef);
            }

            const snapshot = await getDocs(userQuery);
            const fetchedUsers: UserWithStatus[] = snapshot.docs.map(doc => {
                const data = doc.data() as any;
                return {
                    username: data.username || 'Unknown',
                    email: data.email,
                    role: data.role as any,
                    permissions: data.permissions || [],
                    allowedApps: data.allowedApps || ['dfmea'],
                    organization: data.organization,
                    initials: data.initials,
                    status: data.isApproved ? 'active' : (data.role === 'pending' ? 'pending' : 'suspended'),
                    isApproved: data.isApproved,
                    createdDate: data.createdAt?.toDate?.().toISOString().split('T')[0] || 'N/A',
                    lastLogin: data.lastLogin?.toDate?.().toISOString() || null,
                    // Store Doc ID for updates
                    uid: doc.id
                } as any; // Cast for custom fields
            });
            setUsers(fetchedUsers);
        } catch (error) {
            console.error("Error fetching users:", error);
            // alert("Failed to fetch users. Check console.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [currentUser]);

    // --- Actions ---

    // APPROVE USER
    const handleApprove = async () => {
        if (!selectedUser) return;
        const db = getFirestore();
        try {
            // 1. Update Firestore
            // ISO 27001: Provisioning
            await updateDoc(doc(db, 'users', (selectedUser as any).uid), {
                isApproved: true,
                role: 'engineer', // Default approved role
                permissions: ['/dashboard', '/fmeas', '/p-diagram', '/product-structure'], // Default permissions
                allowedApps: selectedApps
            });

            // 2. Log Audit
            await logAuditAction({
                actorId: (currentUser as any).uid || 'unknown',
                actorEmail: currentUser?.email || 'admin',
                action: 'APPROVE_USER',
                targetUserId: (selectedUser as any).uid,
                targetUserEmail: selectedUser.email,
                details: `Approved access for ${selectedUser.username}`
            });

            setIsApproveModalOpen(false);
            fetchUsers(); // Refresh
        } catch (e) {
            console.error(e);
            alert("Failed to approve user.");
        }
    };

    // DELETE USER
    const handleDelete = async () => {
        if (!selectedUser) return;
        const db = getFirestore();
        try {
            // ISO 27001: Deprovisioning
            await deleteDoc(doc(db, 'users', (selectedUser as any).uid));

            // Log Audit
            await logAuditAction({
                actorId: (currentUser as any).uid || 'unknown',
                actorEmail: currentUser?.email || 'admin',
                action: 'DELETE_USER',
                targetUserId: (selectedUser as any).uid,
                targetUserEmail: selectedUser.email,
                details: `Deleted user ${selectedUser.username} (Inactive/Revoked)`
            });

            setIsDeleteModalOpen(false);
            fetchUsers();
        } catch (e) {
            console.error(e);
            alert("Failed to delete user. Ensure you have permission.");
        }
    };

    // --- Filtering & Sorting logic ---
    const getFilteredUsers = () => {
        let result = [...users];

        // Search
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(u =>
                u.username.toLowerCase().includes(lower) ||
                (u.email && u.email.toLowerCase().includes(lower))
            );
        }

        // Status Filter
        if (filterStatus !== 'all') {
            result = result.filter(u => {
                if (filterStatus === 'pending') return !u.isApproved;
                if (filterStatus === 'active') return u.isApproved;
                return true;
            });
        }

        // Inactive Filter (Unused Users)
        if (filterInactive) {
            const ninetyDaysAgo = new Date();
            ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

            result = result.filter(u => {
                if (!u.lastLogin) return true; // Never logged in = inactive
                return new Date(u.lastLogin) < ninetyDaysAgo;
            });
        }

        // Sorting
        result.sort((a, b) => {
            let valA = a[sortField as keyof UserWithStatus];
            let valB = b[sortField as keyof UserWithStatus];

            // Handle undefined/null (push to bottom)
            if (!valA) return 1;
            if (!valB) return -1;

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    };

    const filteredUsers = getFilteredUsers();
    const inactiveCount = users.filter(u => !u.lastLogin || new Date(u.lastLogin) < new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)).length;

    // --- RBAC Checks ---
    const canManageUsers = currentUser?.role === 'autoGainAdmin' || currentUser?.role === 'clientAdmin';
    if (!canManageUsers) {
        return <div className="p-8 text-center text-red-500">Access Denied</div>;
    }

    return (
        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                            <Users className="w-8 h-8 text-primary" />
                            User Access Control
                        </h1>
                        <p className="text-text-secondary mt-1">
                            ISO 27001 Access Management & Provisioning
                        </p>
                    </div>

                    {/* Stats */}
                    <div className="flex gap-4 items-center">
                        <button
                            onClick={() => alert("Invite functionality coming soon!")}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary-dark transition-all shadow-lg"
                        >
                            <UserPlus size={18} /> Invite User
                        </button>
                        <div className="text-center px-4 py-2 bg-background rounded-lg border border-border">
                            <div className="text-2xl font-bold text-orange-500">{users.filter(u => !u.isApproved).length}</div>
                            <div className="text-xs text-text-secondary uppercase tracking-wider">Pending</div>
                        </div>
                        <div className="text-center px-4 py-2 bg-background rounded-lg border border-border">
                            <div className="text-2xl font-bold text-primary">{users.length}</div>
                            <div className="text-xs text-text-secondary uppercase tracking-wider">Total</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="bg-card rounded-lg shadow-sm p-4 border border-border flex flex-col md:flex-row gap-4 justify-between items-center">
                <div className="flex gap-4 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>

                    <select
                        value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value as any)}
                        className="px-4 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending Approval</option>
                        <option value="active">Active</option>
                    </select>

                    <button
                        onClick={() => setFilterInactive(!filterInactive)}
                        className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${filterInactive ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300' : 'bg-background border-border text-text-secondary hover:text-text-primary'}`}
                    >
                        <ShieldAlert className="w-4 h-4" />
                        {filterInactive ? 'Unused / Stale Only' : `Show Unused (${inactiveCount})`}
                    </button>
                </div>

                <button onClick={fetchUsers} className="text-sm text-primary hover:underline">
                    Refresh List
                </button>
            </div>

            {/* List */}
            <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-background border-b border-border">
                            <tr>
                                <th className="px-6 py-3 font-semibold text-text-secondary text-sm">User</th>
                                <th className="px-6 py-3 font-semibold text-text-secondary text-sm">Role</th>
                                <th className="px-6 py-3 font-semibold text-text-secondary text-sm">Status</th>
                                <th
                                    className="px-6 py-3 font-semibold text-text-secondary text-sm cursor-pointer hover:text-primary"
                                    onClick={() => { setSortField('lastLogin'); setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); }}
                                >
                                    Last Login {sortField === 'lastLogin' && (sortOrder === 'asc' ? '↑' : '↓')}
                                </th>
                                <th className="px-6 py-3 font-semibold text-text-secondary text-sm text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-text-secondary">
                                        No users found matching criteria.
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => (
                                    <tr key={user.username} className="hover:bg-background/50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${!user.isApproved ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                                                    }`}>
                                                    {user.initials || user.username.substring(0, 2).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-text-primary">{user.displayName || user.username}</div>
                                                    <div className="text-xs text-text-secondary">{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-text-primary">
                                            <span className="capitalize bg-secondary/10 text-secondary px-2 py-1 rounded">
                                                {user.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {!user.isApproved ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded-full border border-orange-200">
                                                    <AlertCircle className="w-3 h-3" /> Pending Review
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">
                                                    <CheckCircle className="w-3 h-3" /> Approved
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-text-secondary">
                                            {user.lastLogin ? (
                                                <span title={user.lastLogin}>
                                                    {new Date(user.lastLogin).toLocaleDateString()}
                                                </span>
                                            ) : (
                                                <span className="text-text-secondary/50 italic">Never</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                                {!user.isApproved && (
                                                    <button
                                                        onClick={() => { setSelectedUser(user); setIsApproveModalOpen(true); }}
                                                        className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                                        title="Approve User"
                                                    >
                                                        <CheckCircle className="w-5 h-5" />
                                                    </button>
                                                )}

                                                <button
                                                    onClick={() => { setSelectedUser(user); setIsDeleteModalOpen(true); }}
                                                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                                                    title="Delete / Reject User"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* APPROVE MODAL */}
            <Modal isOpen={isApproveModalOpen} onClose={() => setIsApproveModalOpen(false)} title="Grant Access?">
                <div className="space-y-4">
                    <p className="text-text-secondary">
                        Are you sure you want to approve access for <strong>{selectedUser?.email}</strong>?
                    </p>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-sm text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
                        This will assign the standard <strong>Engineer</strong> role. You can modify specific permissions later.
                    </div>

                    <div className="space-y-2 pt-2">
                        <label className="text-sm font-semibold text-text-secondary">Grant Application Access:</label>
                        <div className="flex gap-4">
                            {['dfmea', 'pfmea', 'resolve'].map(app => (
                                <label key={app} className="flex items-center gap-2 text-sm font-medium uppercase cursor-pointer text-text-primary">
                                    <input
                                        type="checkbox"
                                        checked={selectedApps.includes(app)}
                                        onChange={e => {
                                            if (e.target.checked) setSelectedApps([...selectedApps, app]);
                                            else setSelectedApps(selectedApps.filter(a => a !== app));
                                        }}
                                        className="rounded border-border text-primary focus:ring-primary w-4 h-4"
                                    />
                                    {app === 'resolve' ? 'Resolve (AI)' : app}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                        <button onClick={() => setIsApproveModalOpen(false)} className="px-4 py-2 text-text-secondary hover:bg-gray-100 rounded-lg">Cancel</button>
                        <button onClick={handleApprove} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" /> Confirm Approval
                        </button>
                    </div>
                </div>
            </Modal>

            {/* DELETE MODAL */}
            <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Delete User?">
                <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-900 text-red-800 dark:text-red-200">
                        <ShieldAlert className="w-6 h-6 shrink-0" />
                        <div>
                            <div className="font-bold">Permanent Action</div>
                            <div className="text-sm">This action cannot be undone. The user account and profile data will be permanently removed.</div>
                        </div>
                    </div>

                    <p className="text-text-primary">
                        User: <strong>{selectedUser?.username}</strong> ({selectedUser?.email})
                    </p>

                    <div className="flex justify-end gap-3 pt-4">
                        <button onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 text-text-secondary hover:bg-gray-100 rounded-lg">Cancel</button>
                        <button onClick={handleDelete} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2">
                            <Trash2 className="w-4 h-4" /> Confirm Delete
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
